import { useMemo } from 'react'

import { type AllCentralsConfigurationHistoryEvent } from '@/model/firestore/useAllCentralsConfigurationHistory'

export type ConfigurationType = 'assignment' | 'mobileRequests' | 'waitTime' | 'assistedAssignment'

export interface SwitchesByConfigType {
  assignment: number
  mobileRequests: number
  waitTime: number
  assistedAssignment: number
}

export interface RegionSwitchCount {
  regionId: string
  regionName: string
  count: number
}

export interface TimeHeatmapBucket {
  label: string // e.g., "17:00", "17:30"
  startMinutes: number // minutes from midnight
  endMinutes: number
  count: number
}

export interface AllCentralsConfigurationHistoryStats {
  totalSwitches: number
  activeCentralsCount: number
  switchesByConfigType: SwitchesByConfigType
  switchesByRegion: RegionSwitchCount[]
  timeHeatmap: TimeHeatmapBucket[]
}

// Constants for the evening timeline (17:00 - 04:00 next day)
const START_HOUR = 17
const END_HOUR = 4 // Next day
const BUCKET_SIZE_MINUTES = 30

/**
 * Build time heatmap buckets for 30-minute intervals from 17:00 to 04:00.
 */
function buildTimeHeatmapBuckets(): TimeHeatmapBucket[] {
  const buckets: TimeHeatmapBucket[] = []

  // 17:00 to 23:30 (same day)
  for (let hour = START_HOUR; hour < 24; hour++) {
    for (let half = 0; half < 2; half++) {
      const minutes = half * 30
      const startMinutes = hour * 60 + minutes
      buckets.push({
        label: `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
        startMinutes,
        endMinutes: startMinutes + BUCKET_SIZE_MINUTES,
        count: 0,
      })
    }
  }

  // 00:00 to 04:00 (next day, treat as 24:00 - 28:00 for ordering)
  for (let hour = 0; hour <= END_HOUR; hour++) {
    const limit = hour === END_HOUR ? 1 : 2 // Only 04:00 bucket, not 04:30
    for (let half = 0; half < limit; half++) {
      const minutes = half * 30
      const startMinutes = (24 + hour) * 60 + minutes // Use 24+ for next day
      buckets.push({
        label: `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
        startMinutes,
        endMinutes: startMinutes + BUCKET_SIZE_MINUTES,
        count: 0,
      })
    }
  }

  return buckets
}

/**
 * Convert a Date to "minutes from start of evening" for heatmap bucketing.
 * Returns minutes since 17:00 on the operating date.
 */
function getMinutesFromEveningStart(timestamp: Date): number {
  const hour = timestamp.getHours()
  const minutes = timestamp.getMinutes()

  // Hours 0-4 are "next day" in the evening context
  const adjustedHour = hour < 12 ? hour + 24 : hour

  return adjustedHour * 60 + minutes
}

/**
 * Check if an event represents an actual configuration change (not an initial snapshot).
 */
function isActualChange(event: AllCentralsConfigurationHistoryEvent): boolean {
  // Initial snapshots have previousValue === newValue
  return event.previousValue !== event.newValue
}

/**
 * Hook to calculate aggregate statistics across all centrals.
 */
export function useAllCentralsConfigurationHistoryStats(
  events: AllCentralsConfigurationHistoryEvent[],
  regionNames: Record<string, string> // Map of regionId -> regionName
): AllCentralsConfigurationHistoryStats {
  return useMemo(() => {
    // Filter to only actual changes (exclude initial snapshots)
    const actualChanges = events.filter((event) => isActualChange(event))

    // Count switches by config type
    const switchesByConfigType: SwitchesByConfigType = {
      assignment: 0,
      mobileRequests: 0,
      waitTime: 0,
      assistedAssignment: 0,
    }

    for (const event of actualChanges) {
      switchesByConfigType[event.type]++
    }

    // Count switches by region
    const regionCounts: Record<string, number> = {}
    const activeRegions = new Set<string>()

    for (const event of actualChanges) {
      activeRegions.add(event.regionId)
      regionCounts[event.regionId] = (regionCounts[event.regionId] ?? 0) + 1
    }

    const switchesByRegion: RegionSwitchCount[] = Object.entries(regionCounts)
      .map(([regionId, count]) => ({
        regionId,
        regionName: regionNames[regionId] ?? regionId,
        count,
      }))
      .sort((a, b) => b.count - a.count) // Sort by count descending

    // Build time heatmap
    const heatmapBuckets = buildTimeHeatmapBuckets()

    for (const event of actualChanges) {
      const eventMinutes = getMinutesFromEveningStart(event.timestamp)

      // Find the bucket this event falls into
      const bucket = heatmapBuckets.find((b) => eventMinutes >= b.startMinutes && eventMinutes < b.endMinutes)

      if (bucket) {
        bucket.count++
      }
    }

    return {
      totalSwitches: actualChanges.length,
      activeCentralsCount: activeRegions.size,
      switchesByConfigType,
      switchesByRegion,
      timeHeatmap: heatmapBuckets,
    }
  }, [events, regionNames])
}
